import React from 'react';
import StatisticsPieChart from './StatisticsPieChart';
import { getOperarioNombre } from '../utils/helpers';

// Mapeo de colores de Tailwind a valores hexadecimales para SVG
const TAILWIND_COLOR_MAP = {
  'bg-indigo-500': '#6366f1',
  'bg-red-500': '#ef4444',
  'bg-amber-500': '#f59e0b',
  'bg-emerald-500': '#10b981',
  'bg-blue-500': '#3b82f6',
  'bg-purple-500': '#a855f7',
  'bg-pink-500': '#ec4899',
  'bg-teal-500': '#14b8a6',
  'bg-cyan-500': '#06b6d4',
  'bg-lime-500': '#84cc16',
  'bg-orange-500': '#f97316',
  'bg-rose-500': '#f43f5e',
  'bg-fuchsia-500': '#d946ef',
  'bg-sky-500': '#0ea5e9',
  'bg-green-500': '#22c55e',
  'bg-yellow-500': '#eab308',
};

// Colores base para los gráficos, se ciclarán
const CHART_COLOR_CLASSES = [
  'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-yellow-500',
  'bg-red-500', 'bg-indigo-500', 'bg-pink-500', 'bg-teal-500',
  'bg-cyan-500', 'bg-lime-500', 'bg-orange-500', 'bg-rose-500',
  'bg-fuchsia-500', 'bg-sky-500',
];

const StatisticsDashboard = ({ operaciones, operarios, plantas }) => {
  // Función auxiliar para obtener color dinámico
  const getDynamicColor = (index) => {
    const colorClass = CHART_COLOR_CLASSES[index % CHART_COLOR_CLASSES.length];
    return {
      colorClass: colorClass,
      hexColor: TAILWIND_COLOR_MAP[colorClass] || '#cccccc' // Fallback color
    };
  };

  // 1. Estadísticas de Operaciones por Estado
  const statusData = [
    { label: 'Iniciadas', value: operaciones.filter(op => op.estado === 'iniciada').length, colorClass: 'bg-indigo-500', hexColor: TAILWIND_COLOR_MAP['bg-indigo-500'] },
    { label: 'Pausadas', value: operaciones.filter(op => op.estado === 'pausada').length, colorClass: 'bg-red-500', hexColor: TAILWIND_COLOR_MAP['bg-red-500'] },
    { label: 'Pendientes', value: operaciones.filter(op => op.estado === 'pendiente').length, colorClass: 'bg-amber-500', hexColor: TAILWIND_COLOR_MAP['bg-amber-500'] },
    { label: 'Terminadas', value: operaciones.filter(op => op.estado === 'terminada').length, colorClass: 'bg-emerald-500', hexColor: TAILWIND_COLOR_MAP['bg-emerald-500'] },
  ];

  // 2. Unidades Fabricadas por Producto
  const productSummary = operaciones.filter(op => op.estado === 'terminada').reduce((acc, op) => {
    if (op.producto && op.unidades) {
      acc[op.producto] = (acc[op.producto] || 0) + op.unidades;
    }
    return acc;
  }, {});
  const productData = Object.entries(productSummary).map(([product, units], index) => ({
    label: product,
    value: units,
    ...getDynamicColor(index)
  }));

  // 3. Operaciones por Operario (Iniciadas + Terminadas + Pausadas)
  const operarioOpsCount = operarios.map((op, index) => {
    const count = operaciones.filter(o => o.operarioId === op.id && (o.estado === 'iniciada' || o.estado === 'terminada' || o.estado === 'pausada')).length;
    return { label: op.nombre, value: count, ...getDynamicColor(index + 4) }; // Offset para usar otros colores
  }).filter(item => item.value > 0);

  // 4. Operaciones por Planta (Pendientes + Iniciadas + Pausadas)
  const plantOpsCount = plantas.map((plant, index) => {
    const count = operaciones.filter(op =>
      plant.centrosTrabajo.some(ct => ct.id === op.centroTrabajoId) &&
      (op.estado === 'iniciada' || op.estado === 'pendiente' || op.estado === 'pausada')
    ).length;
    return { label: plant.nombre, value: count, ...getDynamicColor(index + 8) }; // Offset para usar otros colores
  }).filter(item => item.value > 0);

  // NUEVA ESTADÍSTICA: 5. Operaciones por Centro de Trabajo (Total)
  const allCentrosTrabajo = plantas.flatMap(p => p.centrosTrabajo);
  const workCenterOpsCount = allCentrosTrabajo.map((ct, index) => {
    const count = operaciones.filter(op => op.centroTrabajoId === ct.id).length;
    return { label: ct.nombre, value: count, ...getDynamicColor(index + 12) };
  }).filter(item => item.value > 0);

  // NUEVA ESTADÍSTICA: 6. Unidades Fabricadas por Operario
  const unitsByOperario = operaciones.filter(op => op.estado === 'terminada').reduce((acc, op) => {
    if (op.operarioId && op.unidades) {
      acc[op.operarioId] = (acc[op.operarioId] || 0) + op.unidades;
    }
    return acc;
  }, {});
  const unitsByOperarioData = Object.entries(unitsByOperario).map(([operarioId, units], index) => ({
    label: getOperarioNombre(operarioId, operarios),
    value: units,
    ...getDynamicColor(index + 16)
  })).filter(item => item.value > 0);

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">Estadísticas Generales</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        <StatisticsPieChart title="Operaciones por Estado" data={statusData} />
        <StatisticsPieChart title="Unidades Fabricadas por Producto" data={productData} />
        <StatisticsPieChart title="Operaciones por Operario (Activas/Terminadas)" data={operarioOpsCount} />
        <StatisticsPieChart title="Operaciones por Planta (Activas/Pendientes)" data={plantOpsCount} />
        <StatisticsPieChart title="Operaciones por Centro de Trabajo (Total)" data={workCenterOpsCount} />
        <StatisticsPieChart title="Unidades Fabricadas por Operario" data={unitsByOperarioData} />
      </div>
    </div>
  );
};

export default StatisticsDashboard;